// plan-06 S04 + plan-10 S03: $KING ticker — bible §24
// Refactored: compact status-bar chip only; full ticker chrome lives inside directory widget header

import { reportDexscreenerFailure, reportDexscreenerSuccess } from '../core/network'

const CACHE_KEY = 'vt.ticker.cache'
const CACHE_TTL_MS = 24 * 60 * 60 * 1000
const REFRESH_MS = 60_000

const API_URL =
  'https://api.dexscreener.com/latest/dex/tokens/4u1xmmG794nNLg3qCFb8ZSek9BAcoNua1ZYcskZrpump'

interface TickerData {
  price: string
  change24h: number
  mcap: number
  ts: number
}

interface CacheEntry {
  data: TickerData
  ts: number
}

let chipEl: HTMLElement | null = null
let refreshTimer: ReturnType<typeof setInterval> | null = null
let abortCtrl: AbortController | null = null
let lastData: TickerData | null = null

function readCache(): CacheEntry | null {
  try {
    const raw = localStorage.getItem(CACHE_KEY)
    if (!raw) return null
    const entry = JSON.parse(raw) as CacheEntry
    if (Date.now() - entry.ts > CACHE_TTL_MS) return null
    return entry
  } catch {
    return null
  }
}

function writeCache(data: TickerData): void {
  try {
    const entry: CacheEntry = { data, ts: Date.now() }
    localStorage.setItem(CACHE_KEY, JSON.stringify(entry))
  } catch {
    // silent
  }
}

function formatChipText(data: TickerData): string {
  const price = parseFloat(String(data.price))
  const priceStr = price >= 0.001 ? '$' + price.toFixed(4) : '$' + price.toFixed(6)
  const pct = data.change24h
  const sign = pct >= 0 ? '+' : ''
  const arrow = pct >= 0 ? ' ↑' : ' ↓'
  return `$KING ${priceStr} ${sign}${pct.toFixed(1)}%${arrow}`
}

function updateChip(): void {
  if (!chipEl || !lastData) return
  chipEl.textContent = formatChipText(lastData)
}

async function fetchTicker(): Promise<void> {
  abortCtrl?.abort()
  abortCtrl = new AbortController()

  try {
    const res = await fetch(API_URL, { signal: abortCtrl.signal })
    if (!res.ok) throw new Error(`status ${res.status}`)

    const json = await res.json()
    const pair = json?.pairs?.[0]
    if (!pair) throw new Error('no pair data')

    const data: TickerData = {
      price: pair.priceUsd ?? '0',
      change24h: parseFloat(pair.priceChange?.h24 ?? '0'),
      mcap: pair.fdv ?? pair.marketCap ?? 0,
      ts: Date.now(),
    }

    writeCache(data)
    lastData = data
    updateChip()
    reportDexscreenerSuccess()
  } catch {
    reportDexscreenerFailure()
    // fail quiet — chip shows last cached value
  }
}

// Mount a compact chip into the status bar (right side)
export function mountTickerStatusBarChip(parent: HTMLElement): void {
  if (chipEl) return

  chipEl = document.createElement('span')
  chipEl.className = 'vt-ticker-chip'
  chipEl.style.cssText = `
    font-family: var(--vt-font-mono);
    font-size: clamp(9px, 0.85vw, 11px);
    color: var(--vt-amber);
    margin-right: 8px;
    white-space: nowrap;
    user-select: none;
  `
  chipEl.textContent = '$KING -- --'

  parent.insertBefore(chipEl, parent.firstChild)

  // paint cached value
  const cached = readCache()
  if (cached) {
    lastData = cached.data
    updateChip()
  }

  // fetch fresh
  fetchTicker()
  refreshTimer = setInterval(fetchTicker, REFRESH_MS)
}

// Get the latest ticker data for other widgets (e.g., directory header)
export function getTickerData(): TickerData | null {
  return lastData
}

export function unmountTicker(): void {
  if (refreshTimer) clearInterval(refreshTimer)
  abortCtrl?.abort()
  if (chipEl?.parentNode) chipEl.parentNode.removeChild(chipEl)
  chipEl = null
}

// Legacy export — now a no-op (was mounting a panel)
export function mountTicker(_parent: HTMLElement): void {
  // no-op — ticker is now a status bar chip
}
